import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/lib/hooks';
import { useAIsearchMutation, useLazyGetNodeQuery, useSearchMutation } from '@/lib/api/GraphAPI';
import { setDocumentResult, setDocumentSearchQuery } from '@/lib/state/slices/ProjectStateSlice';
import {
  CloseButton,
  IconButton,
  Input,
  InputGroup,
  Spinner,
  Stack,
  Icon as IconWrapper,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster';
import { Search } from 'react-feather';
import { Node, Slice } from '@/lib/api/APITypes';
import fetchNodes from '@/lib/Search';
import { DocumentType } from '@/lib/state/ViewTypes';
import { store } from '@/lib/state/Store';

export const SearchField = () => {
  const userId = useAppSelector((state) => state.viewReducer.id);
  const searchType = useAppSelector((state) => state.viewReducer.documents.search.type);
  const searchQueryContext = useAppSelector(
    (state) => state.viewReducer.documents.search.query.CONTEXT,
  );
  const searchQueryExact = useAppSelector(
    (state) => state.viewReducer.documents.search.query.EXACT,
  );
  const searchQueryCurrent = useAppSelector(
    (state) => state.viewReducer.documents.search.query[searchType],
  );
  const filter = useAppSelector((state) => state.viewReducer.documents.filter);

  const [localSearchQuery, setLocalSearchQuery] = useState<string>(searchQueryCurrent); // This local state improves performance
  const [getNodeTrigger] = useLazyGetNodeQuery();
  const dispatch = useAppDispatch();

  const [
    performExactSearch,
    { isLoading: exactSearchIsLoading, isError: exactSearchIsError, error: exactSearchError },
  ] = useSearchMutation();

  const [
    performContextualSearch,
    {
      isError: contextualSearchIsError,
      isLoading: contextualSearchIsLoading,
      error: contextualSearchError,
    },
  ] = useAIsearchMutation();

  useEffect(() => {
    setLocalSearchQuery(store.getState().viewReducer.documents.search.query[searchType]);
  }, [searchType, userId, searchQueryExact, searchQueryContext]);

  useEffect(() => {
    if (exactSearchIsError) {
      toaster.create({
        title: 'Error fetching exact search',
        description: `Something went wrong while fetching exact search: \n${JSON.stringify(exactSearchError)}`,
        type: 'error',
        closable: true,
      });
    }
  }, [exactSearchIsError, exactSearchError]);

  useEffect(() => {
    if (contextualSearchIsError) {
      toaster.create({
        title: 'Error fetching contextual search',
        description: `Something went wrong while fetching contextual search: ${contextualSearchError}`,
        type: 'error',
        closable: true,
      });
    }
  }, [contextualSearchIsError, contextualSearchError]);

  function performSearch(searchType: DocumentType, query: string) {
    dispatch(setDocumentSearchQuery({ type: searchType, query: query }));

    if (query === '') {
      return;
    }

    switch (searchType) {
      case 'CONTEXT':
        performContextualSearch({
          query: query,
          pagination: {
            pageNumber: 0,
            pageSize: 20,
          },
          filter: filter,
        }).then((result) => {
          if (result.data && !result.error && result) {
            const ids = result.data.data.ids.map((id) => id.split(':')[0] + ':' + id.split(':')[1]);

            fetchNodes(ids, getNodeTrigger).then((nodes) => {
              dispatch(
                setDocumentResult({
                  type: 'CONTEXT',
                  value: {
                    ...result.data,
                    data: nodes as unknown,
                  } as Slice<Node[]>,
                }),
              );
            });
          }
        });

        break;

      case 'EXACT':
        performExactSearch({
          query: query,
          pagination: {
            pageNr: 0,
            pageSize: 20,
          },
          filter: filter,
        }).then((result) => {
          if (result.data && !result.error && result) {
            const ids = result.data.data.ids.map((id) => id.split(':')[0] + ':' + id.split(':')[1]);

            fetchNodes(ids, getNodeTrigger).then((nodes) => {
              dispatch(
                setDocumentResult({
                  type: 'EXACT',
                  value: {
                    ...result.data,
                    data: nodes as unknown,
                  } as Slice<Node[]>,
                }),
              );
            });
          }
        });
        break;
    }
  }

  return (
    <Stack direction={'row'} alignItems="center" gap={2} flex="1" width={'100%'} height="100%">
      <InputGroup
        endElementProps={{
          padding: '5px',
        }}
        endElement={
          <CloseButton
            color="secondaryText"
            size="xs"
            borderRadius="0.375rem"
            onClick={() => setLocalSearchQuery('')}
          />
        }
      >
        <Input
          flex={'1'}
          maxWidth="1000px"
          placeholder="Search for a case..."
          borderRadius="0.375rem"
          background="cardDefault"
          color="secondaryText"
          border="none"
          _focus={{ bg: 'cardDefault' }}
          _hover={{ bg: 'cardHover', transition: 'background-color 0.2s' }}
          _active={{ bg: 'cardActive', transition: 'background-color 0.2s' }}
          value={localSearchQuery}
          onChange={(e) => setLocalSearchQuery(e.target.value)}
          disabled={contextualSearchIsLoading || exactSearchIsLoading}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              performSearch(searchType, localSearchQuery);
            }
          }}
        />
      </InputGroup>
      <IconButton
        aria-label="Search"
        borderRadius="1.375rem"
        background="cardDefault"
        color="secondaryText"
        border="none"
        _focus={{ bg: 'cardDefault' }}
        _hover={{ bg: 'cardHover', transition: 'background-color 0.2s' }}
        _active={{ bg: 'cardActive', transition: 'background-color 0.2s' }}
        onClick={(_) => {
          performSearch(searchType, localSearchQuery);
        }}
      >
        {(searchType === 'CONTEXT' && contextualSearchIsLoading) ||
        (searchType === 'EXACT' && exactSearchIsLoading) ? (
          <Spinner color="secondaryText" />
        ) : (
          <IconWrapper as={Search} color="secondaryText" />
        )}
      </IconButton>
    </Stack>
  );
};
